import type {
  FaxItemListQuery,
  FaxItemNeighbors,
} from '../../services/faxItems';
import type { FaxInboxView } from '../../services/faxRouting';

/**
 * The inbox list's filters, as the URL carries them.
 *
 * The list and the review page read the same params, so a review link opens
 * on the same filtered list and its back link returns to it. No patient name
 * goes in the URL: a name is PHI and URLs land in history and logs.
 */

export const PAGE_SIZE = 25;

export interface FaxInboxFilters {
  view: FaxInboxView;
  status?: string;
  from?: string;
  patientId?: string;
  typeId?: string;
  includeFiled: boolean;
}

/** The list's patient chip, carried in history state rather than the URL. */
export interface PatientFilterLabel {
  id: string;
  label: string;
}

/** The one state object every inbox and review navigation carries. */
export interface ReviewNavState {
  neighbors?: FaxItemNeighbors;
  patientLabel?: PatientFilterLabel;
  /** The `kind:id` the reader opened, kept across a self-replace. */
  origin?: string;
}

const VIEWS: FaxInboxView[] = ['working', 'filed', 'archived'];
const STATUSES = ['processing', 'ready', 'failed'];
export const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function uuidOrUndefined(value: string | null): string | undefined {
  return value && UUID.test(value) ? value : undefined;
}

export function readFilters(params: URLSearchParams): FaxInboxFilters {
  const view = params.get('view') as FaxInboxView | null;
  const status = params.get('status');
  const from = params.get('from')?.trim();
  return {
    view: view && VIEWS.includes(view) ? view : 'working',
    status: status && STATUSES.includes(status) ? status : undefined,
    from: from || undefined,
    patientId: uuidOrUndefined(params.get('patient')),
    typeId: uuidOrUndefined(params.get('type')),
    includeFiled: params.get('merged') === '1',
  };
}

/** The 0-based list page in the URL; anything unreadable is the first. */
export function readPage(params: URLSearchParams): number {
  const page = Number(params.get('page'));
  return Number.isInteger(page) && page > 0 ? page : 0;
}

/** The search string for these filters, with defaults left out. */
export function writeFilters(filters: FaxInboxFilters, page = 0): string {
  const params = new URLSearchParams();
  if (filters.view !== 'working') params.set('view', filters.view);
  if (filters.status) params.set('status', filters.status);
  if (filters.from) params.set('from', filters.from);
  if (filters.patientId) params.set('patient', filters.patientId);
  if (filters.typeId) params.set('type', filters.typeId);
  if (filters.includeFiled) params.set('merged', '1');
  if (page > 0) params.set('page', String(page));
  return params.toString();
}

/** The `/fax-items` query for these filters, as the list sends it. */
export function filtersToQuery(filters: FaxInboxFilters): FaxItemListQuery {
  return {
    include_filed: filters.includeFiled,
    status: filters.status,
    fax_number_from: filters.from,
    patient_id: filters.patientId,
    fax_type_id: filters.typeId,
    view: filters.view,
  };
}

/** The list page a 1-based position falls on. */
export function pageOf(position: number): number {
  return Math.max(0, Math.floor((position - 1) / PAGE_SIZE));
}
